import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { Button, Input, Select } from '@/components/ui';
import type { Epic, EpicStatus, InvestibleStructure, Program } from '@/types';

interface EpicFormProps {
  initialData?: Epic | null;
  structures: InvestibleStructure[];
  programs: Program[];
  onSubmit: (data: Partial<Epic>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const EpicForm: React.FC<EpicFormProps> = ({
  initialData,
  structures,
  programs,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userRole = user?.role;
  const canEditIsPrimaryProgram = userRole === 'ADMIN' || userRole === 'PMO';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    requestedTeamWeeks: 0,
    priority: 0,
    status: 'DRAFT' as EpicStatus,
    investibleStructureId: '',
    programId: '',
    isPrimaryProgram: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description || '',
        requestedTeamWeeks: initialData.requestedTeamWeeks,
        priority: initialData.priority || 0,
        status: initialData.status,
        investibleStructureId: initialData.investibleStructureId,
        programId: initialData.programId || '',
        isPrimaryProgram: initialData.isPrimaryProgram || false
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'EPIC name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'EPIC name must be at least 3 characters';
    } else if (formData.name.length > 200) {
      newErrors.name = 'EPIC name must not exceed 200 characters';
    }

    if (!formData.investibleStructureId) {
      newErrors.investibleStructureId = 'Investible Structure is required';
    }

    if (formData.requestedTeamWeeks < 0) {
      newErrors.requestedTeamWeeks = 'Requested team weeks cannot be negative';
    } else if (formData.requestedTeamWeeks > 1000) {
      newErrors.requestedTeamWeeks = 'Requested team weeks seems too high (max 1000)';
    }

    if (formData.priority < 0) {
      newErrors.priority = 'Priority cannot be negative';
    } else if (formData.priority > 999) {
      newErrors.priority = 'Priority cannot exceed 999';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must not exceed 1000 characters';
    }

    // Validation for status transitions
    if (formData.status === 'COMMITTED' || formData.status === 'STRETCH') {
      if (!formData.programId) {
        newErrors.programId = 'Program is required for committed/stretch EPICs';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      description: formData.description || undefined,
      programId: formData.programId || undefined,
      isPrimaryProgram: formData.programId ? formData.isPrimaryProgram : undefined,
      priority: formData.priority || undefined
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const statusOptions = [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'PRIORITIZED', label: 'Prioritized' },
    { value: 'COMMITTED', label: 'Committed' },
    { value: 'STRETCH', label: 'Stretch' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  const structureOptions = structures.map(structure => ({
    value: structure.id,
    label: `${structure.name} (${structure.category})`
  }));

  const programOptions = [
    { value: '', label: 'No primary program' },
    ...programs.filter(p => p.isActive).map(program => ({
      value: program.id,
      label: program.name // Remove category display since all are TECH
    }))
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Input
            label="EPIC Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={errors.name}
            placeholder="e.g., Customer Onboarding Experience"
            required
            fullWidth
          />
        </div>

        <Select
          label="Investible Structure"
          value={formData.investibleStructureId}
          onChange={(e) => handleInputChange('investibleStructureId', e.target.value)}
          options={structureOptions}
          error={errors.investibleStructureId}
          required
          fullWidth
          placeholder="Select structure"
          helperText="The business area this EPIC belongs to"
        />

        <Select
          label="Status"
          value={formData.status}
          onChange={(e) => handleInputChange('status', e.target.value)}
          options={statusOptions}
          fullWidth
          helperText="Current status in the EPIC workflow"
        />

        <Input
          label="Requested Team Weeks"
          type="number"
          value={formData.requestedTeamWeeks.toString()}
          onChange={(e) => handleInputChange('requestedTeamWeeks', parseFloat(e.target.value) || 0)}
          error={errors.requestedTeamWeeks}
          min="0"
          step="0.5"
          fullWidth
          helperText="Estimated effort required in team weeks"
        />

        <Input
          label="Priority"
          type="number"
          value={formData.priority.toString()}
          onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 0)}
          error={errors.priority}
          min="0"
          max="999"
          fullWidth
          helperText="Priority ranking (lower numbers = higher priority)"
        />

        <div className="md:col-span-2">
          <Select
            label="Program"
            value={formData.programId}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange('programId', value);
              // Auto-set isPrimaryProgram to true when program is selected
              if (value && !formData.isPrimaryProgram) {
                setFormData(prev => ({ ...prev, isPrimaryProgram: true }));
              }
              // Clear isPrimaryProgram when program is cleared
              if (!value) {
                setFormData(prev => ({ ...prev, isPrimaryProgram: false }));
              }
            }}
            options={programOptions}
            error={errors.programId}
            fullWidth
            helperText={
              formData.status === 'COMMITTED' || formData.status === 'STRETCH'
                ? 'Required for committed/stretch EPICs'
                : 'The program responsible for delivery'
            }
          />
        </div>

        {canEditIsPrimaryProgram && formData.programId && (
          <div className="md:col-span-2">
            <label className="flex items-center space-x-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPrimaryProgram}
                onChange={(e) => handleInputChange('isPrimaryProgram', e.target.checked)}
                disabled={!formData.programId}
                className="w-4 h-4 text-[var(--accent)] bg-[var(--surface-muted)] border-[var(--border)] rounded focus:ring-[var(--accent)] focus:ring-2"
              />
              <span className="text-[var(--text)]">Mark as Primary Program</span>
            </label>
            <p className="mt-1 text-xs text-[var(--muted)]">
              Indicates this program has primary responsibility for delivery
            </p>
          </div>
        )}

        <div className="md:col-span-2">
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <label className="text-sm font-medium text-[var(--text)]">
                Description
              </label>
              <span className={`text-xs tabular-nums ${formData.description.length > 1000 ? 'text-status-error font-medium' : 'text-[var(--muted)]'}`}>
                {formData.description.length}/1000
              </span>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe the EPIC's scope, objectives, and key deliverables..."
              rows={4}
              className="block w-full min-h-[96px] rounded-md border bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)] placeholder-[var(--muted)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)] border-[var(--border)] hover:border-[var(--accent)]/60"
            />
            {errors.description && (
              <p className="text-xs text-status-error font-medium">{errors.description}</p>
            )}
          </div>
        </div>

      </div>

      {/* Capacity Calculation Preview */}
      {formData.requestedTeamWeeks > 0 && (
        <div className="bg-[var(--surface-muted)] rounded-md p-3">
          <h4 className="text-xs font-semibold text-[var(--text)] uppercase tracking-wide mb-2">Capacity Impact</h4>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-[var(--muted)]">Requested Capacity:</span>
              <span className="ml-2 text-[var(--text)] font-medium">
                {formData.requestedTeamWeeks} team weeks
              </span>
            </div>
            <div>
              <span className="text-[var(--muted)]">Estimated Cost:</span>
              <span className="ml-2 text-[var(--text)] font-medium">
                ${(formData.requestedTeamWeeks * 89 * 40).toLocaleString()}
                <span className="text-[var(--muted)] text-xs ml-1">(blended rate)</span>
              </span>
            </div>
          </div>
          
          {/* Allocation Status for Existing EPICs */}
          {initialData && (
            <div className="mt-2 pt-2 border-t border-[var(--border)]">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-[var(--muted)]">Currently Allocated:</span>
                  <span className="ml-2 text-accent-violet font-medium">
                    {initialData.allocatedTeamWeeks} team weeks
                  </span>
                </div>
                <div>
                  <span className="text-[var(--muted)]">Remaining:</span>
                  <span className={`ml-2 font-medium ${
                    (formData.requestedTeamWeeks - initialData.allocatedTeamWeeks) >= 0
                      ? 'text-status-success'
                      : 'text-status-error'
                  }`}>
                    {formData.requestedTeamWeeks - initialData.allocatedTeamWeeks} team weeks
                  </span>
                </div>
              </div>

              {initialData.allocatedTeamWeeks > 0 && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs text-[var(--muted)] mb-1">
                    <span>Allocation Progress</span>
                    <span>
                      {Math.round((initialData.allocatedTeamWeeks / formData.requestedTeamWeeks) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-[var(--surface-muted)] rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${
                        (initialData.allocatedTeamWeeks / formData.requestedTeamWeeks) > 1
                          ? 'bg-status-error'
                          : (initialData.allocatedTeamWeeks / formData.requestedTeamWeeks) >= 0.8
                          ? 'bg-status-success'
                          : 'bg-accent-blue'
                      }`}
                      style={{
                        width: `${Math.min((initialData.allocatedTeamWeeks / formData.requestedTeamWeeks) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="mt-3">
                <p className="text-xs text-[var(--muted)]">
                  After saving, use the EPIC detail page to manage specific program and team allocations.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-[var(--border)]">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          disabled={isLoading}
        >
          {initialData ? 'Update' : 'Create'} EPIC
        </Button>
      </div>
    </form>
  );
};

export default EpicForm;
